import { HERO } from '@/constants/testIds';
import useReveal from '@/hooks/useReveal';
import { trackEvent } from '@/lib/analytics';

// Cinematic hero — the meat is the undisputed hero of the shot,
// with the diners softly out of focus in the background.
const HERO_IMG = '/hero/trade-dining-v2-1024.webp';
const HERO_SRCSET = [
  '/hero/trade-dining-v2-640.webp 640w',
  '/hero/trade-dining-v2-1024.webp 1024w',
  '/hero/trade-dining-v2-1600.webp 1600w',
].join(', ');

export default function Hero() {
  const reveal = useReveal();

  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="section-light relative pt-16 sm:pt-24 lg:pt-28 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]"
    >
      {/* Warm ambient glow — soft accents for the light theme */}
      <div
        aria-hidden
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] bg-[var(--mw-primary)]/[0.05] rounded-full blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute top-20 right-10 w-[420px] h-[420px] bg-amber-500/[0.08] rounded-full blur-[120px]"
      />

      <div ref={reveal} className="mw-reveal relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center gap-3 mb-10 justify-center">
          <span className="block w-10 h-px bg-black/25" />
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.32em] text-black/65">
            Wholesale · Retail · Food-Service · Export · Malaga WA
          </span>
          <span className="block w-10 h-px bg-black/25" />
        </div>

        <h1
          data-testid={HERO.headline}
          className="font-display font-light text-center leading-[0.98] tracking-tight text-black/95 text-4xl sm:text-6xl lg:text-7xl xl:text-[6rem]"
        >
          The same cuts that feed Perth&apos;s
          <br className="hidden sm:block" />
          <em className="not-italic text-[var(--mw-primary)]">
            top restaurants — on your shelves.
          </em>
        </h1>

        <p
          data-testid="hero-subhead"
          className="mx-auto mt-8 max-w-3xl text-center text-black/70 text-base sm:text-lg leading-relaxed text-balance"
        >
          Halal-certified. MSA-graded. Malaga-cut. One trusted supplier for
          <strong className="text-black font-medium"> restaurants</strong>,
          <strong className="text-black font-medium"> supermarkets</strong>,
          <strong className="text-black font-medium"> retail butchers</strong> and
          <strong className="text-black font-medium"> export partners</strong> — cut to spec,
          delivered on time.
        </p>

        {/* Cinematic hero — hyper-detailed ribeye foreground with diners softly behind */}
        <div
          data-testid="hero-trade-image"
          className="mt-12 sm:mt-14 mx-auto max-w-5xl relative aspect-[16/9] overflow-hidden border border-black/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)]"
        >
          <img
            src={HERO_IMG}
            srcSet={HERO_SRCSET}
            sizes="(min-width: 1280px) 1024px, (min-width: 640px) 90vw, 100vw"
            width="1024"
            height="571"
            alt="Perfectly grilled thick-cut ribeye steak with red-wine jus, roast garlic and grilled asparagus on stoneware — a happy couple dining in a warm candlelit restaurant behind."
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-2xl mx-auto">
          <a
            href="/order"
            data-testid="hero-cta-retail"
            style={{ color: '#fff' }}
            className="group flex-1 inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            Shop Retail
          </a>
          <a
            href="/wholesale"
            data-testid="hero-cta-wholesale"
            style={{ color: '#fff' }}
            className="group flex-1 inline-flex items-center justify-center gap-2 bg-black hover:bg-black/85 text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 7l-8-4-8 4v10l8 4 8-4V7z" />
              <path d="M12 22V12" />
              <path d="M20 7l-8 5-8-5" />
            </svg>
            Wholesale &amp; Trade
          </a>
        </div>

        <div className="mt-6 text-center">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/catalogue.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('Catalogue Downloaded', { source: 'hero', lang: 'en' })}
            data-testid="hero-download-catalogue"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-black/60 hover:text-[var(--mw-primary)] transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download the wholesale catalogue (PDF)
          </a>
        </div>

        {/* Who we supply — trade audience strip */}
        <div className="mt-14 sm:mt-16 max-w-6xl mx-auto">
          <div className="flex items-center gap-3 justify-center mb-5">
            <span className="block w-6 h-px bg-black/25" />
            <span className="text-[9px] uppercase tracking-[0.36em] text-black/50">
              Who we supply
            </span>
            <span className="block w-6 h-px bg-black/25" />
          </div>
          <div
            data-testid="hero-audience-strip"
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
          >
            {[
              { label: 'Restaurants',   sub: 'Fine-dining · Cafés · Caterers' },
              { label: 'Supermarkets',  sub: 'Tray-pack · Cryovac · Private label' },
              { label: 'Retail Butchers', sub: 'Primals · Portioned · Custom' },
              { label: 'Export',        sub: 'AU · MY · SG · UAE' },
            ].map((a) => (
              <div
                key={a.label}
                className="border border-black/10 hover:border-[var(--mw-primary)] bg-white/60 transition-colors p-4 sm:p-5"
              >
                <div className="font-display text-xl sm:text-2xl text-black tracking-tight leading-none">
                  {a.label}
                </div>
                <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-black/55 leading-snug">
                  {a.sub}
                </div>
              </div>
            ))}
          </div>
        </div>

        <dl className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6 max-w-4xl mx-auto">
          {[
            { k: '24yrs', v: 'Trade craft · Est 2001' },
            { k: '−18 / 4°C', v: 'Frozen · Chilled' },
            { k: 'WA', v: 'Local supply' },
            { k: 'Halal', v: 'Certified range' },
          ].map((s, i) => (
            <div
              key={s.v}
              className={`text-center px-2 ${
                i < 3 ? 'sm:border-r sm:border-black/15' : ''
              }`}
            >
              <dt className="font-display text-3xl sm:text-4xl text-black">{s.k}</dt>
              <dd className="mt-2 text-[10px] uppercase tracking-[0.24em] text-black/55">
                {s.v}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
